import { Button, Modal, Number, Spinner } from "@xola/ui-kit";
import { AxiosInstance } from "axios";
import React, { ReactNode, useState } from "react";
import { Except } from "type-fest";
import { ExportJob, JobStatus, Seller } from "types";
import { xolaUri } from "../../api/xolaUri";
import { useSeller } from "../../hooks/useAccount";
import { useResource } from "../../hooks/useResource";
import { NoTranslate } from "./NoTranslate";

interface ExportJobsModalProps {
    job?: ExportJob;
    isOpen: boolean;
    onClose: (e: any) => void;
    apiClient?: AxiosInstance;
}

export const ExportJobModal = ({ job, isOpen, onClose, apiClient }: ExportJobsModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent job={job} apiClient={apiClient} onClose={onClose} />
        </Modal>
    );
};

const getFullUrl = (url: string, seller: Seller): string => {
    if (url.startsWith("http")) {
        return url;
    }

    // URL is relative so most likely this is a non-S3 path in local environment
    return `${xolaUri}${url}?seller=${seller.id}`;
};

const ModalContent = ({ job, onClose, apiClient }: Except<ExportJobsModalProps, "isOpen">) => {
    const seller = useSeller();
    const [refetchInterval, setRefetchInterval] = useState<number | false>(3000);
    const { data: updatedJob } = useResource<ExportJob>(["jobs", job?.id], null, {
        staleTime: 0,
        refetchInterval,
        onSuccess: (data: ExportJob) => {
            const allowed = [JobStatus.COMPLETED, JobStatus.ERROR, JobStatus.STALE];
            if (allowed.includes(data.status)) {
                setRefetchInterval(false);
                window.open(getFullUrl(data.url, seller));
            }
        },
        ...(apiClient ? { api: apiClient } : {}),
    });

    if (!updatedJob?.status && !refetchInterval) {
        setRefetchInterval(3000);
    }

    const StatusContent = job ? statusMap[updatedJob?.status ?? job.status] ?? StaleErrorJobContent : LoadingJobContent;
    const currentStatus = updatedJob?.status ?? job?.status;
    const isDone =
        currentStatus === JobStatus.COMPLETED || currentStatus === JobStatus.ERROR || currentStatus === JobStatus.STALE;
    return (
        <>
            <Modal.Header>Export Report</Modal.Header>
            <Modal.Body data-testid="export-job-modal-body">
                {job ? <StatusContent job={updatedJob ?? job} /> : LoadingJobContent}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={onClose}>{isDone ? "Close" : "Run in Background"}</Button>
            </Modal.Footer>
        </>
    );
};

const NewJobContent = () => {
    return (
        <JobStatusContent status="Waiting in Queue">
            Your report is being processed. We will email you a link to it as soon as it&apos;s ready. If you keep this
            window open, your download will start automatically.
        </JobStatusContent>
    );
};

const ProcessingJobContent = ({ job }: { job: ExportJob }) => {
    return (
        <JobStatusContent status="Processing">
            <div>
                Your report is being processed. We will email you a link to it as soon as it&apos;s ready. If you keep
                this window open, your download will start automatically.
            </div>
            <div>
                <b>Records Processed:</b>&nbsp;
                <NoTranslate>
                    <Number maximumFractionDigits={0}>{job.processedCount}</Number>
                </NoTranslate>
            </div>
        </JobStatusContent>
    );
};

const CompletedJobContent = ({ job }: { job: ExportJob }) => {
    const seller = useSeller();
    const url = getFullUrl(job.url, seller);
    return (
        <JobStatusContent status="Export Complete" spinner={false}>
            <p>
                Your report is ready! It should have already started downloading, but if it has not, you can{" "}
                <a className="text-primary hover:underline" target="_blank" href={url} rel="noreferrer">
                    download it here
                </a>
                . We have also sent you an email with the download link
            </p>
        </JobStatusContent>
    );
};

const StaleErrorJobContent = ({ job }: { job: ExportJob }) => {
    return (
        <JobStatusContent status="Error" spinner={false}>
            <p>Oops, Xola encountered an error while trying to generating your report. Please contact Xola Support.</p>
            <p>
                Job ID: <NoTranslate>{job.id}</NoTranslate>
            </p>
        </JobStatusContent>
    );
};

const LoadingJobContent = () => {
    return (
        <JobStatusContent status="Starting">
            <p>Loading job...</p>
        </JobStatusContent>
    );
};

interface JobStatusContentProps {
    status: string;
    spinner?: boolean;
    children: ReactNode;
}

const JobStatusContent = ({ status, children, spinner = true }: JobStatusContentProps) => {
    return (
        <div className="flex flex-col space-y-2">
            <div className="flex flex-row items-center">
                {status && (
                    <span className="text-gray-darker">
                        <b>Status:</b> {status}
                    </span>
                )}
                {spinner && <Spinner size="tiny" className="ml-2" />}
            </div>
            <div className="space-y-2">{children}</div>
        </div>
    );
};

const statusMap = {
    [JobStatus.NEW]: NewJobContent,
    [JobStatus.PROCESSING]: ProcessingJobContent,
    [JobStatus.COMPLETED]: CompletedJobContent,
    [JobStatus.ERROR]: StaleErrorJobContent,
    [JobStatus.STALE]: StaleErrorJobContent,
};
